import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderOpen, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUS = {
  draft: ["Draft", "bg-slate-100 text-slate-800"],
  submitted: ["Waiting for approval", "bg-amber-100 text-amber-900"],
  approved: ["Approved", "bg-emerald-100 text-emerald-900"],
  rejected: ["Sent back", "bg-rose-100 text-rose-900"],
};

const TABS = [["all", "All"], ["draft", "Drafts"], ["submitted", "Waiting"], ["approved", "Approved"], ["rejected", "Sent back"]];
const PAGE = 10;
const ym = (p) => `${p.year}-${String(p.month).padStart(2, "0")}`;

export function RosterList({ onOpen }) {
  const { actingOrgId } = useAuth();
  const [filter, setFilter] = useState("all");
  const [month, setMonth] = useState("all");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [del, setDel] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["roster-visible", actingOrgId], queryFn: async () => (await api.get("/roster/plans/visible")).data, staleTime: 0, refetchOnMount: "always" });
  const sorted = [...data].sort((a, b) => ym(b).localeCompare(ym(a)) || (b.created_at || "").localeCompare(a.created_at || ""));
  const months = [...new Set(sorted.map(ym))];
  const base = sorted.filter((p) => (month === "all" || ym(p) === month)
    && (!q || `${p.location_name || ""} ${p.department_name || ""} ${p.created_by || ""}`.toLowerCase().includes(q.toLowerCase())));
  const count = (k) => base.filter((p) => k === "all" || (p.status || "draft") === k).length;
  const rows = base.filter((p) => filter === "all" || (p.status || "draft") === filter);
  const reset = (fn) => (v) => { fn(v); setShown(PAGE); };
  return (
    <Card className="mt-5 p-6" data-testid="roster-list">
      <div className="mb-4">
        <p className="text-xl font-bold text-slate-900">Your rosters</p>
        <p className="text-sm text-slate-600">Drafts are visible only to whoever created them and org admins. Submitted and approved rosters are also visible to managers of that location or department.</p>
      </div>
      <div className="mb-3 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-testid="roster-list-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => reset(setFilter)(k)} data-testid={`roster-tab-${k}`}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${filter === k ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>
            {l} <span className="ml-1 text-xs opacity-70">{count(k)}</span>
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={month} onValueChange={reset(setMonth)}>
          <SelectTrigger className="w-44" data-testid="roster-list-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {months.map((m) => <SelectItem key={m} value={m}>{MONTHS[+m.slice(5) - 1]} {m.slice(0, 4)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" value={q} onChange={(e) => reset(setQ)(e.target.value)} placeholder="Search rosters" data-testid="roster-list-search" /></div>
      </div>
      <div className="space-y-2">
        {rows.slice(0, shown).map((p) => <RosterRow key={p.id} p={p} onOpen={onOpen} onDelete={setDel} />)}
        {!rows.length && <p className="text-sm text-slate-500" data-testid="roster-list-empty">{data.length ? "No rosters match these filters." : "No rosters yet."}</p>}
      </div>
      {rows.length > PAGE && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-slate-600" data-testid="roster-list-count">Showing {Math.min(shown, rows.length)} of {rows.length}</span>
          <div className="flex gap-2">
            {shown > PAGE && <Button size="sm" variant="ghost" onClick={() => setShown(PAGE)} data-testid="roster-list-less">Show less</Button>}
            {shown < rows.length && <Button size="sm" variant="outline" onClick={() => setShown(shown + PAGE)} data-testid="roster-list-more">Show 10 more</Button>}
          </div>
        </div>
      )}
      {del && <DeleteDialog p={del} onClose={() => setDel(null)} />}
    </Card>
  );
}

function DeleteDialog({ p, onClose }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const sup = p.can_delete === "super";
  const ok = !sup || typed.trim().toLowerCase() === "delete";
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/roster/plans/${p.id}/delete`, { confirm: typed });
      toast.success(sup ? "Roster deleted" : "Draft cleared");
      qc.invalidateQueries({ queryKey: ["roster-visible"] });
      qc.invalidateQueries({ queryKey: ["roster-approvals"] });
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const name = `${MONTHS[p.month - 1]} ${p.year} · ${p.location_name || ""}${p.department_name ? ` · ${p.department_name}` : ""}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="roster-delete-dialog">
        <DialogHeader>
          <DialogTitle>{sup ? "Delete this roster?" : "Clear this draft?"}</DialogTitle>
          <DialogDescription>
            <b>{name}</b> ({STATUS[p.status || "draft"]?.[0]}) will be removed permanently, including all its shifts{sup && p.status === "approved" ? " and its approved versions" : ""}. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        {sup && (
          <div>
            <p className="mb-1 text-sm text-slate-700">Type <b>delete</b> to confirm</p>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ok && !busy) go(); }} placeholder="delete" autoFocus data-testid="roster-delete-confirm-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="roster-delete-cancel">Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" disabled={!ok || busy} onClick={go} data-testid="roster-delete-submit">{sup ? "Delete roster" : "Clear draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RosterRow({ p, onOpen, onDelete }) {
  const [label, cls] = STATUS[p.status || "draft"] || STATUS.draft;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3" data-testid={`roster-row-${p.id}`}>
      <div className="min-w-0">
        <p className="text-base font-semibold text-slate-900">{MONTHS[p.month - 1]} {p.year} · {p.location_name || "Location"}{p.department_name ? ` · ${p.department_name}` : ""}</p>
        <p className="text-sm text-slate-600">
          Created by {p.created_by || "org admin"}
          {p.approved_by && p.status === "approved" ? ` · approved by ${p.approved_by}` : ""}
          {!p.can_edit ? " · view only" : ""}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className={`rounded-full px-3 py-1 text-sm font-semibold ${cls}`} data-testid={`roster-status-${p.id}`}>{label}</span>
        <Button size="sm" variant="outline" onClick={() => onOpen(p)} data-testid={`roster-open-${p.id}`}><FolderOpen className="mr-1 h-4 w-4" /> Open</Button>
        {p.can_delete && (
          <Button size="sm" variant="outline" className="text-rose-700 hover:bg-rose-50" onClick={() => onDelete(p)} data-testid={`roster-delete-${p.id}`}>
            <Trash2 className="mr-1 h-4 w-4" /> {p.can_delete === "super" ? "Delete" : "Clear draft"}
          </Button>
        )}
      </div>
    </div>
  );
}
